import React, { useState, useEffect, useCallback } from 'react';
import { departmentAPI } from '../../services/api';
import { Button, Input, Alert, Modal, Loading } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import * as permissionUtils from '../../utils/permissions';
import './AdminPages.css';

const DepartmentsPage = () => {
  const { user: currentUser } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    parent: '',
    description: '',
    sort_order: 0,
    is_active: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const response = await departmentAPI.list();
      setDepartments(response.data.results || response.data);
    } catch (err) {
      setMessage({ type: 'error', text: '获取部门数据失败' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      parent: '',
      description: '',
      sort_order: 0,
      is_active: true,
    });
    setEditingDept(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const submitData = {
        ...formData,
        parent: formData.parent || null,
      };

      if (editingDept) {
        await departmentAPI.update(editingDept.id, submitData);
        setMessage({ type: 'success', text: '部门更新成功' });
      } else {
        await departmentAPI.create(submitData);
        setMessage({ type: 'success', text: '部门创建成功' });
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      const errors = err.response?.data;
      const errorMsg = errors?.code?.[0] || errors?.name?.[0] || errors?.detail || '操作失败';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      code: dept.code,
      parent: dept.parent || '',
      description: dept.description || '',
      sort_order: dept.sort_order || 0,
      is_active: dept.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (dept) => {
    if (!window.confirm(`确定要删除部门 "${dept.name}" 吗？`)) {
      return;
    }

    try {
      await departmentAPI.delete(dept.id);
      setMessage({ type: 'success', text: '部门已删除' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: '删除失败，可能存在关联数据' });
    }
  };

  // 构建部门树结构显示
  const buildDeptTree = (depts, parentId = null, level = 0) => {
    return depts
      .filter(d => d.parent === parentId)
      .map(dept => ({
        ...dept,
        level,
        children: buildDeptTree(depts, dept.id, level + 1),
      }));
  };

  const flattenTree = (tree) => {
    let result = [];
    tree.forEach(node => {
      result.push(node);
      if (node.children.length > 0) {
        result = result.concat(flattenTree(node.children));
      }
    });
    return result;
  };

  const deptTree = flattenTree(buildDeptTree(departments));

  if (loading) {
    return <Loading fullScreen text="加载中..." />;
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>部门管理</h1>
          <p>管理公司组织架构</p>
        </div>
        {permissionUtils.canCreateDepartment(currentUser) && (
          <Button onClick={() => { resetForm(); setShowModal(true); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            添加部门
          </Button>
        )}
      </div>

      {message.text && (
        <Alert
          type={message.type}
          message={message.text}
          onClose={() => setMessage({ type: '', text: '' })}
        />
      )}

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>部门名称</th>
              <th>部门编码</th>
              <th>描述</th>
              <th>排序</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {deptTree.length > 0 ? (
              deptTree.map((dept) => (
                <tr key={dept.id}>
                  <td>
                    <span style={{ paddingLeft: `${dept.level * 24}px` }}>
                      {dept.level > 0 && <span className="tree-indent">└─ </span>}
                      {dept.name}
                    </span>
                  </td>
                  <td>{dept.code}</td>
                  <td>{dept.description || '-'}</td>
                  <td>{dept.sort_order}</td>
                  <td>
                    <span className={`status-badge ${dept.is_active ? 'active' : 'inactive'}`}>
                      {dept.is_active ? '启用' : '禁用'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {permissionUtils.canCreateDepartment(currentUser) && (
                        <button className="action-btn" onClick={() => openEditModal(dept)} title="编辑">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      )}
                      {permissionUtils.canCreateDepartment(currentUser) && (
                        <button className="action-btn danger" onClick={() => handleDelete(dept)} title="删除">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="empty-cell">暂无部门数据</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingDept ? '编辑部门' : '添加部门'}
      >
        <form onSubmit={handleSubmit} className="modal-form">
          <Input
            label="部门名称 *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="部门编码 *"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            required
            hint="唯一标识，如：tech_dept"
          />

          <div className="form-group">
            <label className="form-label">上级部门</label>
            <select
              className="form-select"
              value={formData.parent}
              onChange={(e) => setFormData({ ...formData, parent: e.target.value })}
            >
              <option value="">无（顶级部门）</option>
              {departments
                .filter(d => !editingDept || d.id !== editingDept.id)
                .map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">描述</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <Input
            label="排序"
            type="number"
            value={formData.sort_order}
            onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
          />

          <div className="form-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <span>启用部门</span>
            </label>
          </div>

          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              取消
            </Button>
            <Button type="submit" loading={formLoading}>
              {editingDept ? '保存' : '创建'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DepartmentsPage;

